import { Box, Icon, Text, VStack } from '@chakra-ui/react'
import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { addRole, updateRole } from '../../api/classification/classification'
import { WarningIcon } from '../../assets/icons'
import ModalInput from '../forms/modalForms/modalInput'
import FormModal from '../common/FormModal'
import { useFormMutation } from '../../hooks/useFormMutation'

interface AddRoleModalProps {
  isOpen: boolean
  onClose: () => void
  editItem?: any
}

const AddRoleModal: React.FC<AddRoleModalProps> = ({ isOpen, onClose, editItem }) => {
  const { t } = useTranslation()
  const isEdit = !!editItem

  const emptyData = { title: '', description: '' }
  const emptyErrors = { title: false }

  const [data, setData] = useState(emptyData)
  const [errors, setErrors] = useState(emptyErrors)

  useEffect(() => {
    if (editItem) {
      setData({ title: editItem.title || '', description: editItem.description || '' })
    } else {
      setData(emptyData)
    }
    setErrors(emptyErrors)
  }, [editItem, isOpen])

  const resetForm = () => {
    setData(emptyData)
    setErrors(emptyErrors)
  }

  const handleClose = () => {
    resetForm()
    onClose()
  }

  const createMutation = useFormMutation({
    mutationFn: addRole,
    queryKey: 'roles',
    successMessage: t('classifications.toasts.roleCreated'),
    onClose: handleClose,
  })

  const editMutation = useFormMutation({
    mutationFn: updateRole,
    queryKey: 'roles',
    successMessage: t('classifications.toasts.roleUpdated'),
    onClose: handleClose,
  })

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    setData((prev) => ({ ...prev, [name]: value }))
    setErrors((prev) => ({ ...prev, [name]: false }))
  }

  const isValid = () => {
    const newErrors = { title: data.title.trim() === '' }
    setErrors(newErrors)
    return !Object.values(newErrors).some((e) => e)
  }

  const handleSubmit = () => {
    if (isValid()) {
      if (isEdit) {
        editMutation.mutate({ id: editItem.id, title: data.title, description: data.description })
      } else {
        createMutation.mutate({ title: data.title, description: data.description })
      }
    }
  }

  return (
    <FormModal
      isOpen={isOpen}
      onClose={handleClose}
      title={isEdit ? t('classifications.actions.editRole') : t('classifications.actions.addRole')}
      onSubmit={handleSubmit}
      isLoading={createMutation.isPending || editMutation.isPending}
    >
      <VStack spacing={3} align="stretch" w="100%">
        <Box width="100%">
          <ModalInput
            label={t('common.fields.title')}
            type="text"
            name="title"
            placeholder={t('classifications.placeholders.addTitle')}
            value={data.title}
            onChange={handleChange}
            isInvalid={!!errors.title}
          />
          {errors.title && (
            <Box textStyle="b-xs" color="error.100" w="100%" mt={1}>
              <Text>
                <Icon w="16px" h="16px" viewBox="0 0 24 24" fill="none" color="error.100" mt="2px">
                  <WarningIcon />
                </Icon>
                {t('classifications.validation.enterTitle')}
              </Text>
            </Box>
          )}
        </Box>
        <Box width="100%">
          <ModalInput
            label={t('common.fields.description')}
            type="textarea"
            height="128px"
            name="description"
            placeholder={t('classifications.placeholders.addDescription')}
            value={data.description}
            onChange={handleChange}
          />
        </Box>
      </VStack>
    </FormModal>
  )
}

export default AddRoleModal
